import { WORKLIST_COLUMNS_ORIGINAL } from '#pages/order-worklist/constants';
import { LinkOffOutlined, LinkOutlined } from '@vindr/icons';
import { useGetInstitutions } from '@vindr/services/actions/institution.action';
import { useGetOrderWorklistInterval } from '@vindr/services/actions/order.action';
import { ESIGN_STATUS } from '@vindr/services/apis/esign.api';
import { ResponseGetOrderWorklist } from '@vindr/services/apis/order.api';
import { INDICATOR } from '@vindr/services/apis/report.api';
import {
  ESIGN_COLOR,
  STATUS_COLOR,
  STATUS_PRIORITY_COLOR,
} from '@vindr/services/constants/common.constant';
import { ORDER_PRIORITY, ORDER_STATUS } from '@vindr/services/constants/order.constant';
import { MATCH_STATUS } from '@vindr/services/constants/study.constant';
import { DefinedColumns } from '@vindr/services/interfaces';
import ColumnTemplate from '@vindr/ui/src/components/Templates/columns/columnRenderTemplates';
import { PaginationProps } from '@vindr/ui/src/components/ui/Table';
import { formatDateColumn } from '@vindr/utils/dateTime';
import { Pick, getArrayVal } from '@vindr/utils/helpers/common.helper';
import { localStorageAction } from '@vindr/utils/helpers/storage';
import { Tag, Tooltip } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface WorklistColumnsType
  extends DefinedColumns<ReturnType<typeof useGetOrderWorklistInterval>['data']['data'][number]> {}

const defaultColumns: (keyof ResponseGetOrderWorklist['data'][0] | 'index')[] = [
  'index',
  'match_status',
  'priority',
  'patient_id',
  'patient_name',
  'patient_birth_date',
  'gender',
  'status',
  'modality',
  'patient_arrived_time',
  'approved_at',
  'study_desc',
  'diagnostic',
];

const useDefinedColumns = (pagination: PaginationProps): WorklistColumnsType[] => {
  const { t } = useTranslation('Vindoc');

  const { data: institutions, isInitialLoading } = useGetInstitutions({
    limit: 100,
    offset: 0,
  });

  const definedColumns: WorklistColumnsType[] = [
    ColumnTemplate.Index({
      pagination,
      key: 'index',
      dataIndex: 'index',
      width: 60,
    }),
    {
      title: t('Patient ID'),
      width: 100,
      dataIndex: 'patient_id',
      key: 'patient_id',
      ellipsis: true,
    },
    {
      title: t('Priority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      sorter: true,
      ellipsis: true,
      render: (priority) => (
        <Tag
          className="flex w-full justify-center"
          color={STATUS_PRIORITY_COLOR[ORDER_PRIORITY[priority]]}
        >
          {t(ORDER_PRIORITY[priority])}
        </Tag>
      ),
    },
    {
      title: t('Status'),
      width: 110,
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      render: (status) => (
        <Tag className="flex w-full justify-center" color={STATUS_COLOR[ORDER_STATUS[status]]}>
          {t(ORDER_STATUS[status])}
        </Tag>
      ),
    },
    {
      title: t('Patient Name'),
      width: 200,
      dataIndex: 'patient_name',
      key: 'patient_name',
      ellipsis: true,
    },
    {
      title: t('Study Description'),
      dataIndex: 'procedure_name',
      key: 'study_desc',
      ellipsis: true,
      width: 300,
    },
    {
      title: t('Note'),
      dataIndex: 'tech_notes',
      key: 'tech_notes',
      width: 200,
      ellipsis: true,
      render: (tech_notes) => (
        <Tooltip
          placement="topLeft"
          title={tech_notes?.map((note, index) => (
            <div key={index}>{String(`- ${note?.text}`)}</div>
          ))}
        >
          <span title="">{tech_notes?.map((note) => `${String(note?.text)}`)?.join(', ')}</span>
        </Tooltip>
      ),
    },
    {
      title: t('Received At'),
      dataIndex: 'patient_arrived_time',
      key: 'patient_arrived_time',
      width: 140,
      ellipsis: true,
      sorter: true,
      defaultSortOrder: 'descend',
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Started At'),
      dataIndex: 'started_at',
      key: 'started_at',
      width: 140,
      ellipsis: true,
      sorter: true,
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Completed At'),
      dataIndex: 'completed_at',
      key: 'completed_at',
      width: 140,
      ellipsis: true,
      sorter: true,
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Approved At'),
      dataIndex: 'approved_at',
      key: 'approved_at',
      width: 140,
      ellipsis: true,
      sorter: true,
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Delivered At'),
      dataIndex: 'delivered_at',
      key: 'delivered_at',
      width: 140,
      ellipsis: true,
      sorter: true,
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Order Date'),
      dataIndex: 'order_date',
      key: 'order_date',
      width: 140,
      ellipsis: true,
      sorter: true,
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Request Department'),
      dataIndex: 'request_department',
      key: 'request_department',
      width: 180,
      ellipsis: true,
    },
    {
      title: t('Order Group'),
      dataIndex: 'accession_no',
      key: 'accession_no',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Scheduled Date'),
      dataIndex: 'scheduled_date',
      key: 'scheduled_date',
      width: 140,
      ellipsis: true,
      sorter: true,
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Scheduled By'),
      dataIndex: 'scheduled_by_name',
      render: (_, record) => record.scheduled_by_name || record.scheduled_by_username,
      key: 'scheduled_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Modality'),
      dataIndex: 'modality',
      key: 'modality',
      width: 80,
    },
    {
      title: t('Device'),
      dataIndex: 'device_name',
      key: 'device_name',
      width: 100,
      ellipsis: true,
    },
    {
      title: t('Suspended At'),
      dataIndex: 'suspended_at',
      key: 'suspended_at',
      width: 140,
      sorter: true,
      ellipsis: true,
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Suspended By'),
      dataIndex: 'suspended_by_name',
      key: 'suspended_by_name',
      render: (_, record) => record.suspended_by_name || record.suspended_by_username,
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Canceled At'),
      dataIndex: 'cancelled_at',
      key: 'cancelled_at',
      width: 140,
      ellipsis: true,
      sorter: true,
      render: (date) => formatDateColumn(date),
    },
    {
      title: t('Canceled By'),
      dataIndex: 'cancelled_by_name',
      render: (_, record) => record.cancelled_by_name || record.cancelled_by_username,
      key: 'cancelled_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Received By'),
      dataIndex: 'received_by_name',
      render: (_, record) => record.received_by_name || record.received_by_username,
      key: 'received_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Started By'),
      dataIndex: 'started_by_name',
      render: (_, record) => record.started_by_name || record.started_by_username,
      key: 'started_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Completed By'),
      dataIndex: 'completed_by_name',
      render: (_, record) => record.completed_by_name || record.completed_by_username,
      key: 'completed_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Approved By'),
      dataIndex: 'approved_by_name',
      render: (_, record) => record?.approved_by_name || record?.approved_by_username,
      key: 'approved_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Delivered By'),
      dataIndex: 'delivered_by_name',
      render: (_, record) => record.delivered_by_name || record.delivered_by_username,
      key: 'delivered_by_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('Result Delivery Location'),
      dataIndex: 'result_delivery_location',
      key: 'result_delivery_location',
      width: 180,
      ellipsis: true,
    },
    {
      title: t('Visit ID'),
      width: 100,
      dataIndex: 'visit_id',
      key: 'visit_id',
      ellipsis: true,
    },
    {
      title: t('Institution'),
      dataIndex: 'institution_id',
      key: 'institution_id',
      width: 80,
      ellipsis: true,
      render: (institutionId) => {
        const institution = institutions?.data?.find((i) => i.id === institutionId);
        return institution?.name;
      },
    },
    //? Get from Study
    {
      title: t('Study UID'),
      dataIndex: 'study_instance_uid',
      key: 'study_instance_uid',
      width: 80,
      ellipsis: true,
    },
    {
      title: t('Study Date'),
      dataIndex: 'study_date',
      key: 'study_date',
      width: 140,
      ellipsis: true,
      sorter: true,
      render: (date) => formatDateColumn(date),
    },
    //? Get from Order
    {
      title: t('Diagnoses'),
      dataIndex: 'diagnostic',
      key: 'diagnostic',
      width: 120,
      ellipsis: true,
      render: (text) => getArrayVal(text),
    },
    {
      title: t('Match'),
      width: 60,
      align: 'center',
      dataIndex: 'match_status',
      key: 'match_status',
      render: (matched) => (
        <Tooltip
          overlayClassName="toolbar-tooltip"
          placement="bottom"
          title={matched === MATCH_STATUS.Matched ? t('Matched') : t('Unmatched')}
        >
          <span className="flex items-center justify-center">
            {matched === MATCH_STATUS.Matched ? (
              <LinkOutlined className="text-lg text-primary-light" />
            ) : (
              <LinkOffOutlined className="text-lg" />
            )}
          </span>
        </Tooltip>
      ),
    },
    ColumnTemplate.Date({
      title: t('Date of Birth'),
      dataIndex: 'patient_birth_date',
      key: 'patient_birth_date',
      showTime: false,
      width: 110,
      sorter: false,
    }),
    ColumnTemplate.Gender({ width: 60, key: 'gender', dataIndex: 'gender' }),
    {
      title: t('Order Doctor Name'),
      dataIndex: 'order_doctor_name',
      key: 'order_doctor_name',
      width: 80,
      ellipsis: true,
      render: (text) => getArrayVal(text),
    },
    {
      title: t('Referring Physician'),
      dataIndex: 'refer_doctor_name',
      key: 'refer_doctor_name',
      width: 80,
      ellipsis: true,
      render: (text) => getArrayVal(text),
    },
    {
      title: t('Patient Phone'),
      dataIndex: 'patient_phone',
      key: 'patient_phone',
      width: 80,
      ellipsis: true,
      // render: (text) => getArrayVal(text),
    },

    ColumnTemplate.WaitingTime({
      dataIndex: 'patient_arrived_time',
      endIndex: 'started_at',
      resultIndex: 'approved_at',
      sorter: true,
      title: t('Wait Time'),

      detailed: true,
      unit: 'minute',
      requirement: 'optional',
      interval: true,
    }),

    ColumnTemplate.ElapsedTime({
      dataIndex: 'patient_arrived_time',
      endIndex: 'approved_at',
      key: 'total_time',
      sorter: true,
      title: t('Total Time'),

      detailed: true,
      unit: 'minute',
      requirement: 'all',
      interval: true,
    }),

    {
      title: t('Esign Status'),
      width: 100,
      dataIndex: 'esign_status',
      key: 'esign_status',
      ellipsis: true,
      render: (status) => (
        <Tag className="flex w-full justify-center" color={ESIGN_COLOR[ESIGN_STATUS[status]]}>
          {(t(ESIGN_STATUS[status]) as string).toLocaleUpperCase()}
        </Tag>
      ),
    },
    {
      title: t('Report Indicator'),
      width: 100,
      dataIndex: 'latest_indicator',
      key: 'latest_indicator',
      ellipsis: true,
      render: (text) => <span>{(t(INDICATOR[text]) as string).toLocaleUpperCase() || ''}</span>,
    },
    {
      title: t('Assign To'),
      width: 100,
      dataIndex: 'assignee',
      key: 'assignee',
      ellipsis: true,
      render: (_, record) => <span>{record?.assignee?.full_name || ''}</span>,
    },
  ];

  const indexSelectedColumns = Array.isArray(window?.config?.columns?.worklist)
    ? window?.config?.columns?.worklist
    : defaultColumns;
  const sorted = definedColumns.sort((a, b) => {
    const indexA = indexSelectedColumns.indexOf(a.key);
    const indexB = indexSelectedColumns.indexOf(b.key);

    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  const columnDefault = [];

  for (const col of indexSelectedColumns) {
    const column = definedColumns.find((item) => item.key === col);
    if (column) columnDefault.push(Pick(column, ['title', 'key', 'dataIndex', 'width']));
  }
  localStorageAction.set(WORKLIST_COLUMNS_ORIGINAL, { resizableColumns: columnDefault });

  return useMemo(() => sorted, [isInitialLoading, t]);
};

export default useDefinedColumns;
